import React, { useState, useEffect, useCallback } from "react";
import { IRoom } from "../../models/room/Room";
import { FieldName, ProfileData, ProfileEditForm } from "./profileEditForm";
import { IGuest } from "../../models/base/Guest";
import { withErrorBoundary } from "@fidget/common/components/errorBoundary";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { EditMeLabel } from "../editMeLabel/editMeLabel";
import styles from "./profileComponent.css";
import classNames from "classnames";
import { LocationData, BrowserData, BackofficeData, anyRenderableData } from "./profileInfo";
import { observer } from "mobx-react";
import { GuestStatusIndicator } from "../status/GuestStatusIndicator";
import { DropdownSection } from "./dropdownSection";
import { useStore } from "../../models";
import { AppPermission, ArtichokePresence } from "@closerplatform/spinner-openapi";

interface Props {
  room: IRoom;
}

const profileComponent = observer(({ room }: Props) => {
  const { uiStore } = useStore();
  const [editMode, setEditMode, selectedField, profile, cancelEditing, startEditing] =
    useProfileInfoState(room.owner);

  const handleFormSubmit = async (data: ProfileData) => {
    const { phoneNumber, phoneRegion, ...rest } = data;
    await profile!.update(
      {
        phone:
          phoneNumber && phoneRegion ? { number: phoneNumber.replace(/[^+\d]/g, ""), region: "ZZ" } : undefined,
        ...rest
      },
      true
    );
    setEditMode(false);
  };

  const renderEditingContactInfo = () => {
    return (
      <ProfileEditForm
        room={room}
        initial={profile}
        onSubmit={handleFormSubmit}
        selectedField={selectedField}
      >
        <AgentButton type="submit" fullWidth id="profile_edit_save_button">
          <L10n id={"settings.apply"} />
        </AgentButton>
        <AgentButton onClick={cancelEditing} fullWidth buttonStyle={"outlined secondary"}>
          <L10n id={"settings.discard"} />
        </AgentButton>
      </ProfileEditForm>
    );
  };

  const renderContactInfo = () => {
    const { owner } = room;
    return (
      owner !== undefined && (
        <>
          <div className={styles["displaying-container"]}>
            <EditMeLabel
              value={owner.firstName}
              fakeValue={owner.fakeFirstName}
              onClick={() => startEditing("firstName")}
            />
            <EditMeLabel
              value={owner.lastName}
              fakeValue={owner.fakeLastName}
              onClick={() => startEditing("lastName")}
            />
            <EditMeLabel
              value={owner.email}
              placeholder={intl.formatMessage("profile.addEmail")}
              onClick={() => startEditing("email")}
            />
            <EditMeLabel
              value={owner.phoneNumber}
              placeholder={intl.formatMessage("profile.addPhone")}
              onClick={() => startEditing("phoneRegion")}
            />
          </div>
        </>
      )
    );
  };

  const renderUneditableProfileInfo = () => {
    const {
      owner: { firstName, fakeFirstName, lastName, fakeLastName, email, phoneNumber }
    } = room;

    return (
      <div>
        <p className={styles["contact-uneditable-field-name"]}>{firstName || fakeFirstName}</p>
        <p className={styles["contact-uneditable-field-name"]}>{lastName || fakeLastName}</p>
        {!!email && <p className={styles["contact-uneditable-field"]}>{email}</p>}
        {!!phoneNumber && <p className={styles["contact-uneditable-field"]}>{phoneNumber}</p>}
      </div>
    );
  };

  return (
    <div className={classNames(styles["profile-edit"], { [styles.editing]: editMode })}>
      <PresenceInfo presence={room.owner.status} />
      <DropdownSection title={intl.formatMessage("profile.contactInfo")}>
        {uiStore.canDisplayWithPermission(AppPermission.LeadUpdateProfile)
          ? editMode
            ? renderEditingContactInfo()
            : renderContactInfo()
          : renderUneditableProfileInfo()}
      </DropdownSection>
      <DropdownSection title={intl.formatMessage("profile.locationData")}>
        <LocationData profile={room.owner} />
      </DropdownSection>
      {room.owner.browserData.length > 0 && (
        <DropdownSection title={intl.formatMessage("profile.browserData")}>
          <BrowserData profile={room.owner} />
        </DropdownSection>
      )}
      {room.owner.additionalData.length > 0 && anyRenderableData(room.owner.additionalData) && (
        <DropdownSection title={intl.formatMessage("profile.additionalData")}>
          <BackofficeData profile={room.owner} />
        </DropdownSection>
      )}
    </div>
  );
});

const PresenceInfo = ({ presence }: { presence: ArtichokePresence }) => {
  return (
    <div className={styles["presence-info"]}>
      {presence === ArtichokePresence.Available ? (
        <>
          <L10n id={"profile.guestPresence.online"} />
          <GuestStatusIndicator status={presence} />
        </>
      ) : (
        <L10n id={"profile.guestPresence.offline"} />
      )}
    </div>
  );
};

const useProfileInfoState = (
  currentProfile: IGuest
): [
  boolean,
  (mode: boolean) => void,
  FieldName,
  IGuest,
  () => void,
  (field: FieldName) => void
] => {
  const [editMode, setEditMode] = useState<boolean>(false);
  const [selectedField, setSelectedField] = useState<FieldName>("firstName");
  const [profile, setProfile] = useState<IGuest>(currentProfile);

  const startEditing = (field: FieldName) => {
    setProfile(currentProfile);
    setSelectedField(field);
    setEditMode(true);
  };

  const cancelEditing = () => {
    setEditMode(false);
  };

  const handleKeyUp = useCallback(
    (ev: KeyboardEvent) => {
      // if escape pressed, exit editing
      if (ev.keyCode === 27 && editMode) {
        cancelEditing();
      }
      // if 'e' pressed outside any inputs/textareas, begin editing
      else if (
        ev.key === "e" &&
        !(ev.altKey || ev.ctrlKey || ev.metaKey || ev.shiftKey) &&
        !["INPUT", "TEXTAREA"].includes((ev.target as HTMLElement).tagName)
      ) {
        startEditing("firstName");
      }
    },
    [editMode, profile]
  ); // update callbacks on state update

  useEffect(() => {
    window.addEventListener("keyup", handleKeyUp);
    return () => window.removeEventListener("keyup", handleKeyUp); // return is something like onUnmount
  }, [handleKeyUp]); // update listeners on callback change

  useEffect(() => {
    setProfile(currentProfile);
    setEditMode(false);
  }, [currentProfile]); // update state on room change

  return [editMode, setEditMode, selectedField, profile, cancelEditing, startEditing];
};

export const ProfileComponent = withErrorBoundary(profileComponent);
